/* eslint-disable react/no-children-prop */
'use client'
import { DestPagesProps, SanityData, SearchItem } from '@/app/types'
import dynamic from 'next/dynamic'
import { FC, useCallback, useEffect, useRef, useState } from 'react'

import Booking from '@/app/components/Booking'
import CompactBooking from '@/app/components/CompactBooking'
import HotelHeaderNavigation from '@/app/components/HotelHeaderNavigation'
import InitCaller from '@/app/components/InitCaller'
import { DisplayData } from '@/app/components/LocationDataHandler'
import Overlay from '@/app/components/Overlay'
import Top from '@/app/components/Top'
import SiteHeading from '@/app/components/SiteHeading'
import { AvailableInputTypes, SMALL_HEADER_FORM, XL_BREAKPOINT } from '@/app/constants'
import eventBus from '@/app/eventBus'
import { useGrowthbook } from '@/app/hooks/use-growthbook'
import { useMapView } from '@/app/hooks/useMapView'
import { PS_EVENTS } from '@/app/ps-events'
import { parseSearchParamsToLower } from '@/app/utils'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import classNames from 'classnames'
import HotelsLoadingAnimation from './HotelsLoadingAnimation'

const LocationDataHandler = dynamic(
	() => import('@/app/components/LocationDataHandler'),
	{ ssr: false }
)

export interface Props {
	sanityData?: SanityData
	mainPage?: boolean
	isHotelLandingPage?: boolean
	hotelPage?: boolean
	hotel?: any
	params?: DestPagesProps
	loading?: boolean
	blogPage?: boolean
	initialSelectedItem?: SearchItem | null
	/** Server-rendered city name for SEO - used to pre-render the H1 */
	serverCity?: string
	/** Server-translated heading text for SEO - prevents flash of default content */
	serverHeading?: string
}
const isDesktop = () => typeof window !== 'undefined' && window.matchMedia(XL_BREAKPOINT).matches;

const Header: FC<Props> = (props) => {
	const {
		sanityData,
		mainPage,
		hotelPage,
		params,
		loading,
		isHotelLandingPage,
		blogPage,
		serverCity,
		serverHeading,
	} = props
	const [targetLocation, setTargetLocation] = useState<SearchItem | null>(
		props.initialSelectedItem || null
	)
	const [displayValue, setDisplayValue] = useState<DisplayData | null>(null)
	const [userId, setUserId] = useState<string | null>(null)
	const { growthbook, gbCallback } = useGrowthbook(!!mainPage)
	// Will be used with GrowthBook
	const [isSticky, setIsSticky] = useState(!hotelPage)
	const searchParams = parseSearchParamsToLower(params?.searchParams)
	const destCode = searchParams?.destcode
	const lang = params?.params.lang
	const isExpandedRef = useRef(false)
	const [forceUpdate, setForceUpdate] = useState(false)
	const [openedInput, setOpenedInput] = useState<AvailableInputTypes>(null)
	const prevSearchParamsRef = useRef<string>('')
	const headerRef = useRef<HTMLDivElement>(null)
	const [isAutoScrolling, setIsAutoScrolling] = useState(false)
	const { isFullScreenMap } = useMapView();

	const getTargetLocation = (item: SearchItem): SearchItem => {
		setTargetLocation(item)
		return item
	}

	const getDisplayValue = (item: DisplayData): DisplayData => {
		setDisplayValue(item)
		return item
	}

	const handleToggleExpand = () => {
		isExpandedRef.current = !isExpandedRef.current
		setForceUpdate((prev) => !prev)
		setOpenedInput(null)
	}

	// Keeping for future GB experiments
	useEffect(() => {
		if (userId) {
			gbCallback({ uid: userId })
		}
	}, [userId])

	// Reset map/list view state on home. The `home_page_view` GA event is NOT
	// emitted here — RenderGA (pageName='home_page') is the single source of
	// truth for every `${pageName}_view`, matching result_page/hotel_page. Firing
	// it here too double-sourced the event and corrupted the funnel denominator.
	useEffect(() => {
		if (props.mainPage) {
			eventBus.publish(PS_EVENTS.FULL_SCREEN_MAP, false)
			// Seed the next results page to the hotels list, not the split map view,
			// so a search from the home page lands on the list by default.
			eventBus.publish(PS_EVENTS.LIST_VIEW, true)
		}
	}, [])

	const handleScroll = useCallback(() => {
		const scrollPosition = window.scrollY

		const hotelPageStickyTreshold = 80

		let stickyTreshold = 120

		if (hotelPage) {
			stickyTreshold = hotelPageStickyTreshold
			//Only update sticky state if it's not an auto-scroll
			if (!isAutoScrolling) {
				setIsSticky(scrollPosition > stickyTreshold)
				if (isExpandedRef.current && scrollPosition > 100) {
					isExpandedRef.current = false
					setForceUpdate((prev) => !prev)
				}
			}
		}
	}, [isAutoScrolling])

	useEffect(() => {
		const handleAutoScroll = () => {
			setIsAutoScrolling(true)
			setTimeout(() => setIsAutoScrolling(false), 1000) // Adjust timeout as needed
		}

		eventBus.subscribe(PS_EVENTS.AUTO_SCROLL, handleAutoScroll)

		const handleClickOutside = (event: MouseEvent) => {
			if (
				headerRef.current &&
				!headerRef.current.contains(event.target as Node) &&
				isExpandedRef.current
			) {
				isExpandedRef.current = false
				setForceUpdate((prev) => !prev)
			}
		}

		if (!mainPage) {
			window.addEventListener('scroll', handleScroll)
		}
		document.addEventListener('mousedown', handleClickOutside)

		return () => {
			window.removeEventListener('scroll', handleScroll)
			document.removeEventListener('mousedown', handleClickOutside)
			eventBus.unsubscribe(PS_EVENTS.AUTO_SCROLL, handleAutoScroll)
		}
	}, [handleScroll, mainPage])

	useEffect(() => {
		if (isExpandedRef.current) {
			setOpenedInput(SMALL_HEADER_FORM.STICKY_HEADER)
		} else {
			setOpenedInput(null)
		}
	}, [isExpandedRef.current])

	const isDefaultLocation =
		!mainPage || destCode || isHotelLandingPage ? targetLocation : null
	const shouldLoadLocationData = !mainPage || destCode || isHotelLandingPage

	const shouldShowHotelNavigation = hotelPage && isSticky

	const mainPageHeading =
		mainPage ? (
			<SiteHeading
				hotel={props.hotel}
				showNewHeading={true}
				customHeading={!!destCode}
				selectedItem={targetLocation}
				isLandingPage={isHotelLandingPage}
				sanityData={sanityData}
				serverCity={serverCity}
				serverHeading={serverHeading}
			/>
		) : null

	useEffect(() => {
		// Closing the header after searchParams have actually changed
		const currentSearchParamsString = JSON.stringify(searchParams)

		if (isExpandedRef.current) {
			if (currentSearchParamsString !== prevSearchParamsRef.current && !searchParams?.cal) {
				isExpandedRef.current = false
				setForceUpdate((prev) => !prev)
				setOpenedInput(null)
			}
		}

		prevSearchParamsRef.current = currentSearchParamsString
	}, [searchParams])

	return (
		<>
			<GrowthBookProvider growthbook={growthbook}>
				{/* Hotel Header Navigation - only render on hotel pages */}
				{hotelPage && (
					<HotelHeaderNavigation
						isSticky={isSticky}
						isVisible={shouldShowHotelNavigation}
					/>
				)}

				{/* Regular Header */}
				<header
					ref={headerRef}
					className={classNames('header', {
						'header--sticky': !mainPage && (isSticky || isExpandedRef.current),
						'header--expanded': isExpandedRef.current,
						'bg-white': !mainPage,
						'hidden': !mainPage && isFullScreenMap && isDesktop(),
						'header--hidden': shouldShowHotelNavigation,
					})}
					data-test-id="header"
				>
					<Top
						lang={lang as string}
						isMain={mainPage}
						isSticky={isSticky}
						blogPage={blogPage}
						children={{
							middle: !mainPage && !blogPage && (
								<CompactBooking
									displayValue={displayValue || ({ displayValue: targetLocation?.displayname } as DisplayData)}
									defaultLocation={isDefaultLocation}
									isMainPage={!!mainPage}
									sanityData={sanityData}
									loading={loading}
									customHeading={!!destCode}
									isHotelPage={hotelPage}
									isExpanded={isExpandedRef.current}
									onToggleExpand={handleToggleExpand}
									dest={params?.params.dest}
								/>
							),
							bottom: !mainPage && !blogPage && (
								<div
									className={classNames(`compact-booking__content`, {
										[`compact-booking__content--expanded`]:
											isExpandedRef.current,
										[`compact-booking__content--collapsed`]:
											!isExpandedRef.current,
									})}
								>
									<Booking
										displayValue={displayValue || ({} as DisplayData)}
										defaultLocation={isDefaultLocation}
										isMainPage={mainPage}
										sanityData={sanityData}
										loading={loading}
										customHeading={!!destCode}
										isHotelPage={hotelPage}
										isExpanded={isExpandedRef.current}
										onClose={() => {
											isExpandedRef.current = false
											setForceUpdate((prev) => !prev)
										}}
									/>
								</div>
							),
						}}
					/>
				</header>
				<div
					className={classNames('header-wrapper', {
						hidden: isFullScreenMap && isDesktop() && !mainPage,
						'max-lg:py-3 max-lg:px-2': mainPage,
					})}
				>
					<InitCaller
						sanityData={sanityData}
						setUserId={(id: string) => setUserId(id)}
						isHomePage={mainPage as boolean}
					/>
					{shouldLoadLocationData && (
						<LocationDataHandler
							hotelPage={hotelPage}
							params={props.params?.params as DestPagesProps['params']}
							searchParams={
								props.params?.searchParams as DestPagesProps['searchParams']
							}
							setDisplayValue={getDisplayValue}
							setTargetLocation={getTargetLocation}
							mainPage={mainPage}
						/>
					)}
					{mainPage && (
						<>
							<div className="text-center max-lg:hidden">
								{mainPageHeading}
							</div>
							<Booking
								hotel={props.hotel}
								displayValue={displayValue || ({} as DisplayData)}
								defaultLocation={isDefaultLocation}
								isMainPage={mainPage}
								sanityData={sanityData}
								loading={loading}
								customHeading={!!destCode}
								isHotelPage={hotelPage}
								isHotelLandingPage={isHotelLandingPage}
								mobileHeadingSlot={mainPageHeading}
							/>
						</>
					)}
					{props.loading ? <HotelsLoadingAnimation /> : null}
				</div>
				<Overlay
					isOpened={openedInput !== null}
					openInput={handleToggleExpand}
					onClick={handleToggleExpand}
				/>
			</GrowthBookProvider>
		</>
	)
}

export default Header
